<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'

const { locale } = useI18n()
const session = useVisitorSession()
const config = useRuntimeConfig().public
const api = useAPI()
const apiBaseCookie = useCookie<string | null>('apiBase', { default: () => null, path: '/', sameSite: 'lax', maxAge: 60 * 60 * 24 * 180 })
const open = ref(false)
const endpoint = ref('')
const error = ref('')
// The reload can take a while (for example while the dev server restarts), so show that it started.
const reloading = ref(false)
const isZh = computed(() => locale.value === 'zh')
const options = ['https://hkls2.apida.net', '/api', 'http://127.0.0.1:3333/api']

watch(open, (value) => {
  if (value) {
    endpoint.value = api.apiBase()
    error.value = ''
  }
})

function saveEndpoint() {
  if (!config.isDev || !session.realAdmin) return
  const value = endpoint.value.trim().replace(/\/+$/, '')
  if (value !== '/api') {
    let url: URL
    try {
      url = new URL(value)
    } catch {
      error.value = isZh.value ? '请输入完整的 HTTP(S) 地址，或 /api。' : 'Enter a full HTTP(S) URL or /api.'
      return
    }
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
      error.value = isZh.value ? '地址只支持 HTTP(S)，不能包含账号、密码、查询参数或片段。' : 'Use HTTP(S) without credentials, a query or a fragment.'
      return
    }
  }
  apiBaseCookie.value = value
  reloading.value = true
  // Reload so SSR, session restoration and mounted views use the same backend.
  reloadNuxtApp({ force: true })
}

function resetAPIEndpoint() {
  if (!config.isDev || !session.realAdmin) return
  apiBaseCookie.value = null
  reloading.value = true
  reloadNuxtApp({ force: true })
}
</script>

<template>
  <Dialog v-if="config.isDev && session.realAdmin" v-model:open="open">
    <DialogTrigger as-child><slot /></DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{{ $t('api-endpoint.header') }}</DialogTitle>
        <DialogDescription>{{ isZh ? '保存后重新载入页面。此浏览器的 dev 环境会使用所选 API。' : 'Saving reloads the page. This browser uses the selected API in dev.' }}</DialogDescription>
      </DialogHeader>
      <form class="grid gap-4" @submit.prevent="saveEndpoint">
        <div class="grid gap-2">
          <label for="api-endpoint">API Endpoint</label>
          <Input id="api-endpoint" v-model="endpoint" autocomplete="off" spellcheck="false" />
        </div>
        <p v-if="error" role="alert" class="text-destructive">{{ error }}</p>
        <div class="flex flex-wrap gap-2">
          <Button v-for="addr in options" :key="addr" type="button" variant="outline" size="sm" :disabled="reloading" class="max-w-full whitespace-normal break-all" @click="endpoint = addr; error = ''">{{ addr }}</Button>
        </div>
        <div class="flex flex-wrap gap-2">
          <Button type="submit" :disabled="reloading">{{ reloading ? (isZh ? '正在重新载入…' : 'Reloading…') : $t('save') }}</Button>
          <Button type="button" variant="outline" :disabled="reloading" @click="resetAPIEndpoint">{{ isZh ? '恢复默认' : 'Restore default' }}</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
</template>
